import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, errMsg } from "../../lib/api";
import { CATS } from "../Header";

const EMPTY = { title: "", category: "clothing", price_usd: "", original_price_usd: "", stock: 10, image: "" };

const ProductForm = ({ initial, onClose, onSaved }) => {
  const [f, setF] = useState(initial ? { ...EMPTY, ...initial } : EMPTY);
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    const body = { title: f.title, category: f.category, price_usd: Number(f.price_usd), original_price_usd: f.original_price_usd ? Number(f.original_price_usd) : undefined, stock: Number(f.stock), image: f.image };
    try {
      initial ? await api.patch(`/admin/products/${initial.id}`, body) : await api.post("/admin/products", body);
      toast.success(initial ? "Product updated" : "Product added"); onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const inp = (k, ph, type = "text") => <input data-testid={`product-form-${k}`} required={k !== "original_price_usd"} type={type} step="0.01" placeholder={ph} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="h-11 rounded-xl border px-3 w-full text-sm" />;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="product-form-dialog">
        <DialogHeader><DialogTitle className="font-display">{initial ? "Edit product" : "Add product"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          {inp("title", "Product title")}
          <select data-testid="product-form-category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="h-11 rounded-xl border px-3 w-full text-sm">{CATS.map(([s, n]) => <option key={s} value={s}>{n}</option>)}</select>
          <div className="grid grid-cols-3 gap-2">{inp("price_usd", "Price $", "number")}{inp("original_price_usd", "Was $", "number")}{inp("stock", "Stock", "number")}</div>
          {inp("image", "Image URL (https://...)", "url")}
          {f.image && <img src={f.image} alt="" className="w-20 h-20 rounded-lg object-cover" />}
          <button data-testid="product-form-save" disabled={busy} className="w-full h-11 rounded-full bg-[#C85A32] text-white font-semibold disabled:opacity-50">{busy ? "Saving..." : "Save"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const AdminProducts = ({ onChange }) => {
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [page, setPage] = useState(1);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/products", { params: { q: q || undefined, category: cat || undefined, page, limit: 20, sort: "newest" } }).then((r) => setData(r.data));
  useEffect(() => { load(); }, [cat, page]); // eslint-disable-line
  const del = async (p) => {
    if (!window.confirm(`Delete "${p.title.slice(0, 40)}"?`)) return;
    try { await api.delete(`/admin/products/${p.id}`); toast.success("Product deleted"); load(); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-6" data-testid="admin-products">
      <div className="flex flex-wrap gap-2 items-center">
        <form onSubmit={(e) => { e.preventDefault(); setPage(1); load(); }}><input data-testid="admin-product-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products" className="h-10 rounded-full border px-4 text-sm" /></form>
        <select data-testid="admin-product-category" value={cat} onChange={(e) => { setCat(e.target.value); setPage(1); }} className="h-10 rounded-full border px-4 text-sm bg-white"><option value="">All categories</option>{CATS.map(([s, n]) => <option key={s} value={s}>{n}</option>)}</select>
        <span className="text-sm text-stone-500">{data.total.toLocaleString()} products</span>
        <button data-testid="admin-add-product-btn" onClick={() => setEdit({})} className="ml-auto h-10 px-5 rounded-full bg-[#C85A32] text-white text-sm font-semibold flex items-center gap-2"><Plus className="w-4 h-4" /> Add product</button>
      </div>
      <div className="bg-white rounded-2xl border border-[#C85A32]/10 mt-4 divide-y">
        {data.items.map((p) => (
          <div key={p.id} className="flex items-center gap-3 p-3" data-testid={`admin-product-${p.id}`}>
            <img src={p.image} alt="" className="w-12 h-12 rounded-lg object-cover" />
            <div className="flex-1 min-w-0"><p className="text-sm truncate">{p.title}</p><p className="text-xs text-stone-500">{p.category} · stock {p.stock}</p></div>
            <span className="font-semibold text-sm w-20 text-right">${p.price_usd.toFixed(2)}</span>
            <button data-testid={`admin-edit-product-${p.id}`} onClick={() => setEdit(p)} className="p-2 rounded-full hover:bg-stone-100"><Pencil className="w-4 h-4" /></button>
            <button data-testid={`admin-delete-product-${p.id}`} onClick={() => del(p)} className="p-2 rounded-full hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-center gap-3 mt-4">
        <button data-testid="admin-products-prev" disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-4 h-9 rounded-full border bg-white disabled:opacity-40 text-sm">Previous</button>
        <span className="text-sm">Page {page} of {data.pages}</span>
        <button data-testid="admin-products-next" disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="px-4 h-9 rounded-full border bg-white disabled:opacity-40 text-sm">Next</button>
      </div>
      {edit && <ProductForm initial={edit.id ? edit : null} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); onChange(); }} />}
    </div>
  );
};
